import 'regenerator-runtime/runtime';
import { call, select, take, takeLatest } from 'redux-saga/effects';
import { AnyAction } from 'redux';
import {
  GlobalActions,
  GlobalPut,
  QuoteDbDashboardPayload,
  QuoteDbInitPayload,
  QuoteDbSelectedAppsItem,
} from '@ps20-frontend/shop-integration-redux';
import { Dialogschritt } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { App } from '@ps20-frontend/frontend-models';
import { IAppState } from '../appState';
import { selectVorvertragByVertragsnummerAndSpLaufnummer } from '../selectors/vorvertragsUebersicht.selector';
import { angebotIsTarifiert } from '../selectors/warenkorb.selector';

export function* quoteDbSaga() {
  yield takeLatest(
    GlobalActions.QUOTEDB_DASHBOARD_DATA_REQUESTED,
    safeSaga(getQuoteDbData, App.SHOP_DASHBOARD),
  );
  yield takeLatest(
    GlobalActions.IN_DASHBOARD_UEBERNEHMEN,
    safeSaga(quoteDbInit, App.SHOP_DASHBOARD),
  );
  yield takeLatest(
    GlobalActions.ZAHLUNGSOPTIONEN_CHANGED,
    safeSaga(quoteDbInit, App.SHOP_DASHBOARD),
  );
  yield takeLatest(GlobalActions.VEREINBARUNGEN_CHANGED, safeSaga(quoteDbInit, App.SHOP_DASHBOARD));
  yield takeLatest(
    GlobalActions.VERTRAGSLAUFZEIT_CHANGED,
    safeSaga(quoteDbInit, App.SHOP_DASHBOARD),
  );
  yield takeLatest(GlobalActions.PRODUKT_DELETED, safeSaga(quoteDbInit, App.SHOP_DASHBOARD));
  yield takeLatest(
    GlobalActions.TARIFIERUNG_TRIGGERED_BEIBEHALTUNG,
    safeSaga(quoteDbInit, App.SHOP_DASHBOARD),
  );
}

export function* getQuoteDbData() {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  let ersparnis = 0;
  let kombinachlass = 0;

  const selectedApps: Array<QuoteDbSelectedAppsItem> = state.warenkorb
    // ToDo: Entfernen sobald QuoteDb bei diesen Sparten angebungen wird
    .filter(
      produkt =>
        produkt.appId !== App.JHP && produkt.appId !== App.GEGE && produkt.appId !== App.TGV,
    )
    .map(selectedProdukt => {
      const isErsatz =
        selectedProdukt.vorvertragsNummern != null && selectedProdukt.vorvertragsNummern.length > 0;
      const appItem: QuoteDbSelectedAppsItem = {
        appId: selectedProdukt.appId,
        angebotsId: selectedProdukt.angebotsId,
        laufzeit: selectedProdukt.laufzeit,
        ersatzInfo: {
          isErsatz,
          beibehalten: selectedProdukt.beibehalten,
        },
      };
      if (isErsatz) {
        const summeJahresNettoVorvertraege = selectedProdukt.vorvertragsNummern
          .map(vorvertragsNummer =>
            selectVorvertragByVertragsnummerAndSpLaufnummer(
              state,
              vorvertragsNummer.vertragsnummer,
              vorvertragsNummer.spLaufnummer,
            ),
          )
          .map(vorvertrag => vorvertrag && vorvertrag.jahresbestandpraemieNetto)
          .filter(netto => netto !== null)
          .reduce((netto1, netto2) => netto1 + netto2, 0);
        appItem.ersatzInfo.jahresNettoVorvertrag = summeJahresNettoVorvertraege;
      }
      return appItem;
    });

  const produkteInWarenkorb = state.warenkorb.filter(angebotIsTarifiert);

  if (produkteInWarenkorb.length > 0) {
    ersparnis = produkteInWarenkorb
      .map(produkt => produkt.bruttopreisOhneKombiRabatt - produkt.bruttopreis)
      .reduce((a, b) => a + b, 0);
    for (const produkt of produkteInWarenkorb) {
      if (produkt.rabattstufe > 0) {
        kombinachlass = produkt.rabattstufe;
        break;
      }
    }
  }

  const quoteDbPayload: QuoteDbDashboardPayload = {
    selectedApps,
    ersparnis,
    kombinachlass,
    unfallZaehlstuecke: state.unfallZaehlstueck && state.unfallZaehlstueck.zaehlstuecke,
  };
  yield call(globalPut.put, new GlobalActions().quoteDBDataDashboardGenerated(quoteDbPayload));
}

export function* quoteDbInit(action: AnyAction) {
  const state: IAppState = yield select();
  const globalPut = GlobalPut.getInstance();
  const tarifierteAngebote = state.warenkorb.filter(angebotIsTarifiert);

  for (const _angebot of tarifierteAngebote) {
    yield take(GlobalActions.PREISE_INS_DASHBOARD_UEBERNEHMEN);
  }
  yield take(GlobalActions.GESAMTPREIS_CHANGED);

  const quoteDbInitPayload: QuoteDbInitPayload = {
    ausloeserAngebotsId: {
      appId: action.payload.appId,
      angebotsId: action.payload.angebotsId,
    },
    dialogschritt: mapDialogschritt(action),
  };

  yield call(globalPut.put, new GlobalActions().quoteDBInit(quoteDbInitPayload));
}

function mapDialogschritt(action: AnyAction): Dialogschritt {
  const actionType = action.type;
  if (actionType === GlobalActions.IN_DASHBOARD_UEBERNEHMEN) {
    return Dialogschritt.SEITE_2_UEBERNEHMEN;
  } else if (actionType === GlobalActions.VEREINBARUNGEN_CHANGED) {
    return Dialogschritt.DASHBOARD_VEREINBARUNGEN;
  } else if (actionType === GlobalActions.VERTRAGSLAUFZEIT_CHANGED) {
    return Dialogschritt.DASHBOARD_AENDERUNG_LAUFZEIT;
  } else if (actionType === GlobalActions.PRODUKT_DELETED) {
    return Dialogschritt.DASHBOARD_ANGEBOT_LOESCHEN;
  } else if (actionType === GlobalActions.TARIFIERUNG_TRIGGERED_BEIBEHALTUNG) {
    return Dialogschritt.DASHBOARD_BEIBEHALTUNG;
  }
  return Dialogschritt.DASHBOARD_AENDERUNG_ZAHLUNGSART;
}
